import { useMutationObserver, useResizeObserver } from '@vueuse/core'
import { Ref, onMounted, onUnmounted, ref } from 'vue'

const toArray = (maybeCollection: HTMLCollection | undefined) => Array.from(maybeCollection ?? [])

type Top = number
type Height = number
type Bottom = number
type Row = { top: Top; maxHeight: Height; maxBottom: Bottom }

export const useRows = (containerRef: Ref<HTMLElement | undefined>) => {
    const rows = ref<Row[]>([])

    const updateRows = () => {
        rows.value = getRows(containerRef.value?.children)
    }

    const { stop: stopMutationObserver } = useMutationObserver(containerRef, updateRows, { childList: true })

    const { stop: stopResizeObserver } = useResizeObserver(containerRef, updateRows)

    onMounted(updateRows)

    onUnmounted(() => {
        stopMutationObserver()
        stopResizeObserver()
    })

    return rows
}

const getRows = (collection: HTMLCollection | undefined) => {
    const rowsMap = new Map<Top, { maxHeight: Height; maxBottom: Bottom }>()

    for (const element of toArray(collection)) {
        const { top, height, bottom } = element.getBoundingClientRect()

        const row = rowsMap.get(top) ?? { maxHeight: 0, maxBottom: -Infinity }

        if (height > row.maxHeight) {
            row.maxHeight = height
        }
        if (bottom > row.maxBottom) {
            row.maxBottom = bottom
        }

        rowsMap.set(top, row)
    }

    return Array.from(rowsMap.entries()).map(([top, row]) => ({ top, ...row }))
}
